import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sunrise, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { api } from '../lib/api';

// ---------------------------------------------------------------------------
// "Lesperance is the French word for Hope" campaign theme components.
// Lesperance comes from the French idea of hope — l'espérance.
// Used sparingly and classily: warm gold accents on the existing navy design.
// All content is admin-editable via Settings → Hope Theme (hope_theme setting).
// ---------------------------------------------------------------------------

export const useHopeTheme = (preloaded) => {
  const [hope, setHope] = useState(preloaded || null);
  useEffect(() => {
    if (preloaded) { setHope(preloaded); return; }
    api.get('/settings').then((r) => setHope(r.data?.hope_theme || {})).catch(() => setHope({}));
  }, [preloaded]);
  return hope || {};
};

// Small gold badge — hero + reusable
export const HopeBadge = ({ text = 'Lesperance is the French word for Hope', className = '' }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/50 px-3 py-1 text-xs font-bold tracking-wide text-[#C9A84C] backdrop-blur-sm ${className}`} data-testid="hope-hero-badge">
    <Sunrise className="h-3.5 w-3.5" /> {text}
  </span>
);

// Thin tagline strip shown directly under the hero
const DEFAULT_STRIP_BUTTONS = [
  { label: 'Donate', url: '/contact', style: 'gold' },
  { label: 'Volunteer', url: '/volunteer', style: 'outline' },
];

export const HopeTaglineStrip = ({ block }) => {
  if (!block || block.show === false || !block.text) return null;
  const buttons = (block.buttons || DEFAULT_STRIP_BUTTONS).filter((b) => b && b.show !== false && b.label && b.url);
  return (
    <div className="bg-[#1a2744] border-t border-white/10" data-testid="hope-tagline-strip">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-4">
        <p className="text-center text-xs sm:text-sm font-semibold text-[#e8d9ae] tracking-wide">
          {block.text}
        </p>
        {buttons.length > 0 && (
          <span className="flex items-center gap-2 shrink-0">
            {buttons.map((b, i) => (
              <Button
                key={i}
                asChild
                size="sm"
                className={`h-10 sm:h-8 px-4 sm:px-3.5 text-xs font-bold btn-press ${b.style === 'outline' ? 'bg-transparent border border-[#C9A84C]/60 text-[#e8d9ae] hover:bg-white/10' : 'bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744]'}`}
                data-testid={`hope-strip-btn-${(b.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <Link to={b.url}>{b.label}</Link>
              </Button>
            ))}
          </span>
        )}
      </div>
    </div>
  );
};

// The main homepage "Lesperance is the French word for Hope" section
export const HopeHomepageSection = ({ hope }) => {
  const block = hope?.homepage;
  if (!block || block.show === false) return null;
  return (
    <section className="relative bg-[#1a2744] py-14 md:py-20 overflow-hidden" data-testid="hope-homepage-section">
      <div className="absolute inset-0 noise-overlay" />
      <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#C9A84C]/10 blur-3xl" aria-hidden="true" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <HopeBadge text={hope?.hero_badge?.text || 'Lesperance is the French word for Hope'} />
        <h2 className="mt-4 font-serif font-black text-3xl sm:text-4xl text-white" data-testid="hope-section-title">{block.title || 'Lesperance is the French word for Hope'}</h2>
        {block.subtitle && <p className="mt-2 text-[#C9A84C] font-bold text-sm sm:text-base tracking-wide" data-testid="hope-section-subtitle">{block.subtitle}</p>}
        {hope?.meaning_note && <p className="mt-3 text-xs text-slate-400 italic" data-testid="hope-meaning-note">{hope.meaning_note}</p>}
        <p className="mt-5 text-slate-200 leading-relaxed max-w-2xl mx-auto" data-testid="hope-section-text">{block.text}</p>
        {(block.points || []).length > 0 && (
          <ul className="mt-6 space-y-2 text-left max-w-xl mx-auto" data-testid="hope-section-points">
            {block.points.map((p, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm sm:text-base text-slate-100">
                <CheckCircle2 className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" /> {p}
              </li>
            ))}
          </ul>
        )}
        {block.cta_text && (
          <Button asChild className="mt-8 h-12 px-7 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold text-base btn-press" data-testid="hope-section-cta">
            <Link to={block.cta_link || '/volunteer'}>{block.cta_text}</Link>
          </Button>
        )}
      </div>
    </section>
  );
};

// Generic warm inline section used on Why I'm Running / Platform / Issue Tracker
export const HopeInlineSection = ({ block, points, testid = 'hope-inline-section', meaningNote }) => {
  if (!block || block.show === false) return null;
  return (
    <section className="my-8" data-testid={testid}>
      <div className="rounded-2xl border border-[#C9A84C]/40 bg-[#f6f4ee] p-6 sm:p-8">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#8a6f22]">
          <Sunrise className="h-3.5 w-3.5" /> Lesperance is the French word for Hope
        </span>
        <h2 className="mt-2 font-serif font-black text-2xl sm:text-3xl text-[#1a2744]" data-testid={`${testid}-title`}>{block.title}</h2>
        <p className="mt-3 text-slate-700 leading-relaxed whitespace-pre-line" data-testid={`${testid}-text`}>{block.text}</p>
        {(points || block.points || []).length > 0 && (
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2" data-testid={`${testid}-points`}>
            {(points || block.points).map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="h-4 w-4 text-[#8a6f22] shrink-0 mt-0.5" /> {p}
              </li>
            ))}
          </ul>
        )}
        {meaningNote && <p className="mt-4 text-xs text-slate-500 italic">{meaningNote}</p>}
      </div>
    </section>
  );
};

// Small headline block for the homepage map section
export const HopeMapHeadline = ({ block }) => {
  if (!block || block.show === false) return null;
  return (
    <div className="mb-5" data-testid="hope-map-headline">
      <p className="inline-flex items-center gap-1.5 text-sm font-bold text-[#8a6f22]">
        <Sunrise className="h-4 w-4" /> {block.title || 'A Map of Hope and Accountability'}
      </p>
      <p className="text-sm text-slate-600 leading-relaxed mt-1 max-w-3xl">{block.text}</p>
    </div>
  );
};
